
import React, { useState, useEffect } from "react";
import "./menu_sql.css";
import { Button, Table, Input, notification, Select } from "antd";
import { FaCheck } from "react-icons/fa6";
import { MdDeleteForever } from "react-icons/md";
import { IoMdAddCircleOutline } from "react-icons/io";
import { IoCloseCircleOutline } from "react-icons/io5";
import axios from "axios";
import { Resizable } from "react-resizable";
import type { ResizeCallbackData } from "react-resizable";

const MenuSql: React.FC = () => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };


    const ResizableTitle = (props: React.HTMLAttributes<any> & { onResize: (e: React.SyntheticEvent, data: ResizeCallbackData) => void; width: number; }) => {
        const { onResize, width, ...restProps } = props;
        if (!width) return <th {...restProps} />;
        return (
            <Resizable
                width={width}
                height={0}
                handle={
                    <span
                        className="react-resizable-handle"
                        onClick={(e) => e.stopPropagation()}
                    />
                }
                onResize={onResize}
                draggableOpts={{ enableUserSelectHack: false }}
            >
                <th {...restProps} />
            </Resizable>
        );
    };


    const components = {
        header: {
            cell: ResizableTitle,
        },
    };

    const [tables, setTables] = useState<string[]>([]);
    const [selectedTable, setSelectedTable] = useState<string>("");
    const [dbData, setDbData] = useState<any[]>([]);
    const [gridData, setGridData] = useState<any[]>([]);
    const [columns, setColumns] = useState<any[]>([]);
    const [primaryKeys, setPrimaryKeys] = useState<string[]>([]);
    const [deletedRows, setDeletedRows] = useState<any[]>([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [searchTable, setSearchTable] = useState<string>("");
    const [filterColumn, setFilterColumn] = useState<string>("");
    const [filterValue, setFilterValue] = useState<string>("");

    useEffect(() => {
        fetchTables();
    }, []);

    const fetchTables = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/sql/tables`);
            setTables(res.data);
        } catch (err: any) {
            notification.error({ message: "Gagal memuat tabel", description: err.message });
        }
    };

    const fetchTableData = async (tableName: string) => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/sql/table/${tableName}`);
            const { columns: schemaCols, rows, primaryKeys: pks } = res.data;
            setDbData(JSON.parse(JSON.stringify(rows)));
            setGridData(rows);
            setPrimaryKeys(pks);
            setDeletedRows([]);
            setSelectedRowKeys([]);
            setFilterColumn("");
            setFilterValue("");

            const calculateColumnWidths = (colsList: any[], dataRows: any[]) => {
                const FONT_STYLE = "14px monospace";
                const PADDING = 32;
                const getTextWidth = (text: string): number => {
                    const canvas = document.createElement("canvas");
                    const context = canvas.getContext("2d");
                    if (context) {
                        context.font = FONT_STYLE;
                        return context.measureText(text).width;
                    }
                    return 0;
                };
                return colsList.map((col: any) => {
                    const headerWidth = getTextWidth(col.column_name) + PADDING;
                    const cellWidths = dataRows.map(row => {
                        const cellText = row[col.column_name] === null ? "" : String(row[col.column_name]);
                        return getTextWidth(cellText) + PADDING;
                    });
                    return Math.ceil(Math.max(headerWidth, ...cellWidths, 100));
                });
            };
            const initialWidths = calculateColumnWidths(schemaCols, rows);

            const formattedCols = schemaCols.map((col: any, idx: number) => ({
                title: col.column_name,
                dataIndex: col.column_name,
                key: col.column_name,
                width: initialWidths[idx],
                render: (text: any, record: any) => (
                    <Input
                        value={text === null ? "" : text}
                        bordered={false}
                        style={{ padding: "4px", fontFamily: "monospace" }}
                        onChange={(e) => handleCellChange(record, col.column_name, e.target.value)}
                    />
                )
            }));
            setColumns(formattedCols);
        } catch (err: any) {
            notification.error({ message: "Gagal memuat data tabel", description: err.message });
        }
    };

    const handleSelectTable = (tbl: string) => {
        setSelectedTable(tbl);
        fetchTableData(tbl);
    };

    const handleResize = (index: number) => (
        e: React.SyntheticEvent,
        { size }: ResizeCallbackData
    ) => {
        setColumns((prevColumns) => {
            const newColumns = [...prevColumns];
            newColumns[index] = {
                ...newColumns[index],
                width: size.width,
            };
            return newColumns;
        });
    };

    const resizableColumns = columns.map((col, index) => ({
        ...col,
        onHeaderCell: (column: any) => ({
            width: column.width,
            onResize: handleResize(index),
        }),
    }));

    const tableWidth = columns.reduce((sum, col) => sum + (col.width as number || 100), 0);

    const filteredGridData = gridData.filter(row => {
        if (!filterValue) return true;
        if (filterColumn) {
            const val = row[filterColumn];
            return String(val ?? "").toLowerCase().includes(filterValue.toLowerCase());
        }
        return Object.keys(row).some(key =>
            key !== "_tempId" && String(row[key] ?? "").toLowerCase().includes(filterValue.toLowerCase())
        );
    });

    const handleCellChange = (record: any, columnName: string, value: any) => {
        setGridData(prev => prev.map(row => {
            if (row._tempId && row._tempId === record._tempId) {
                return { ...row, [columnName]: value };
            }
            const isMatch = primaryKeys.length > 0
                ? primaryKeys.every(pk => row[pk] === record[pk])
                : JSON.stringify(row) === JSON.stringify(record);
            if (isMatch) {
                return { ...row, [columnName]: value };
            }
            return row;
        }));
    };

    const handleAdd = () => {
        if (!selectedTable) {
            notification.warning({ message: "Pilih tabel terlebih dahulu!" });
            return;
        }
        const newRow: any = { _tempId: "new_" + Date.now() };
        columns.forEach(col => {
            newRow[col.key] = "";
        });
        setGridData(prev => [...prev, newRow]);
    };

    const handleDelete = () => {
        if (!selectedTable) return;
        if (selectedRowKeys.length === 0) {
            notification.warning({ message: "Pilih baris yang ingin dihapus!" });
            return;
        }
        const rowsToDelete = gridData.filter(row => selectedRowKeys.includes(getRowKey(row)));
        const remainingRows = gridData.filter(row => !selectedRowKeys.includes(getRowKey(row)));
        const existingRowsToDelete = rowsToDelete.filter(row => !row._tempId);
        setDeletedRows(prev => [...prev, ...existingRowsToDelete]);
        setGridData(remainingRows);
        setSelectedRowKeys([]);
    };

    const handleApply = async () => {
        if (!selectedTable) return;
        const added = gridData.filter(row => row._tempId);
        const updated: any[] = [];
        gridData.forEach(row => {
            if (row._tempId) return;
            const original = dbData.find(orig => {
                return primaryKeys.length > 0
                    ? primaryKeys.every(pk => orig[pk] === row[pk])
                    : JSON.stringify(orig) === JSON.stringify(row);
            });
            if (original && JSON.stringify(original) !== JSON.stringify(row)) {
                updated.push({ oldRow: original, newRow: row });
            }
        });

        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/sql/table/${selectedTable}/apply`, {
                added,
                updated,
                deleted: deletedRows,
                primaryKeys
            });
            notification.success({ message: "Perubahan berhasil diterapkan!" });
            fetchTableData(selectedTable);
        } catch (err: any) {
            notification.error({ message: "Gagal menyimpan perubahan", description: err.response?.data?.error || err.message });
        }
    };

    const handleDiscard = () => {
        if (selectedTable) {
            fetchTableData(selectedTable);
            notification.info({ message: "Perubahan lokal dibuang." });
        }
    };

    const getRowKey = (row: any) => {
        if (row._tempId) return row._tempId;
        return primaryKeys.length > 0
            ? primaryKeys.map(pk => row[pk]).join("-")
            : JSON.stringify(row);
    };

    return (
        <div id="if9y" style={transformStyle}>
            <div className="gjs-grid-row" id="id8ye">
                <div className="gjs-grid-column" id="i2e7g">
                    <div style={{ display: "flex", height: "550px", border: "1px solid #d9d9d9", background: "#fff", fontFamily: "monospace" }}>
                        <div style={{ width: "250px", borderRight: "1px solid #d9d9d9", overflowY: "auto", background: "#f5f5f5", padding: "10px" }}>
                            <h4 style={{ margin: "0 0 10px 0", borderBottom: "1px solid #ddd", paddingBottom: "5px", color: "#333" }}>Tables (amp)</h4>
                            <Input
                                placeholder="Search tables..."
                                value={searchTable}
                                onChange={(e) => setSearchTable(e.target.value)}
                                style={{ marginBottom: "10px" }}
                                allowClear
                            />
                            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                                {tables.filter(tbl => tbl.toLowerCase().includes(searchTable.toLowerCase())).map(tbl => (
                                    <li
                                        key={tbl}
                                        onClick={() => handleSelectTable(tbl)}
                                        style={{
                                            padding: "6px 10px",
                                            cursor: "pointer",
                                            background: selectedTable === tbl ? "#1890ff" : "transparent",
                                            color: selectedTable === tbl ? "#fff" : "#333",
                                            borderRadius: "4px",
                                            marginBottom: "2px",
                                            fontSize: "13px"
                                        }}
                                    >
                                        {tbl}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                            {selectedTable ? (
                                <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto", padding: "10px" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}> 
                                        <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "bold" }}>Table: {selectedTable}</h3> 
                                        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                                            <Select
                                                placeholder="All Fields"
                                                value={filterColumn || undefined}
                                                onChange={(val) => setFilterColumn(val || "")}
                                                style={{ width: "150px" }}
                                                allowClear
                                            >
                                                {columns.map(col => (
                                                    <Select.Option key={col.key} value={col.key}>
                                                        {col.title}
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                            <Input
                                                placeholder="Search value..."
                                                value={filterValue}
                                                onChange={(e) => setFilterValue(e.target.value)}
                                                style={{ width: "200px" }}
                                                allowClear
                                            />
                                        </div>
                                    </div>
                                    <Table
                                        dataSource={filteredGridData}
                                        columns={resizableColumns as any}
                                        pagination={{ pageSize: 50, showSizeChanger: true }}
                                        size="small"
                                        bordered
                                        rowKey={getRowKey}
                                        rowSelection={{
                                            selectedRowKeys,
                                            onChange: (keys) => setSelectedRowKeys(keys)
                                        }}
                                        scroll={{ x: tableWidth }}
                                        components={components}
                                    />
                                </div>
                            ) : (
                                <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#888", fontSize: "14px" }}>
                                    Select a table from the list to view data
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="gjs-grid-row" id="i5mvh">
                <div className="gjs-grid-column" id="i139y">
                    <Button className="gr-button" onClick={handleAdd}>
                        <IoMdAddCircleOutline style={{ width: '1em', height: '1em', marginRight: '8px' }} />
                        Add
                    </Button>
                    <Button className="gr-button" onClick={handleDelete}>
                        <MdDeleteForever style={{ width: '1em', height: '1em', marginRight: '8px' }} />
                        Delete
                    </Button>
                    <Button className="gr-button" onClick={handleApply}>
                        <FaCheck style={{ width: '1em', height: '1em', marginRight: '8px' }} />
                        Apply
                    </Button>
                    <Button className="gr-button" onClick={handleDiscard}>
                        <IoCloseCircleOutline style={{ width: '1em', height: '1em', marginRight: '8px' }} />
                        Discard
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default MenuSql;